import { type ElementType } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { PluginGroup } from '@/plugins/types';

export interface ToolCardProps {
  id: string;
  name: string;
  description: string;
  icon: ElementType;
  href?: string;
  group?: PluginGroup;
  badge?: string;
  badgeVariant?: 'default' | 'secondary' | 'outline' | 'destructive';
  isReadOnly?: boolean;
  onClick?: () => void;
  compact?: boolean;
  disabled?: boolean;
}

const GROUP_STYLES: Record<PluginGroup, { bg: string; text: string; label: string }> = {
  analysis: { bg: 'bg-primary/10', text: 'text-primary', label: 'Analyse' },
  data: { bg: 'bg-amber-500/10', text: 'text-amber-500', label: 'Daten' },
  admin: { bg: 'bg-destructive/10', text: 'text-destructive', label: 'Admin' },
  core: { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Core' },
};

export function ToolCard({
  id,
  name,
  description,
  icon: Icon,
  href,
  group,
  badge,
  badgeVariant = 'secondary',
  isReadOnly = false,
  onClick,
  compact = false,
  disabled = false,
}: ToolCardProps) {
  const groupStyle = group ? GROUP_STYLES[group] : null;
  const cardClassName = cn(
    'group block rounded-xl border border-border/60 bg-card p-4 transition-all duration-200',
    !disabled && 'hover:border-primary/50 hover:shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.15)] hover:-translate-y-0.5',
    disabled && 'cursor-not-allowed opacity-60',
    compact && 'p-3'
  );

  const content = (
    <>
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
            groupStyle?.bg ?? 'bg-muted',
            compact && 'h-8 w-8'
          )}
        >
          <Icon className={cn('h-5 w-5', groupStyle?.text ?? 'text-muted-foreground', compact && 'h-4 w-4')} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className={cn('font-semibold truncate', compact ? 'text-sm' : 'text-sm')}>
              {name}
            </h3>
            {isReadOnly && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-muted-foreground border-muted-foreground/40">
                Nur Lesen
              </Badge>
            )}
            {badge && (
              <Badge variant={badgeVariant} className="text-[10px] px-1.5 py-0">
                {badge}
              </Badge>
            )}
          </div>
          {!compact && (
            <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
              {description}
            </p>
          )}
        </div>

        <ArrowUpRight
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground/40 transition-all duration-200',
            !disabled && 'group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5'
          )}
        />
      </div>

      {!compact && groupStyle && group !== 'core' && (
        <div className="mt-3 flex items-center justify-between">
          <Badge variant="outline" className={cn('text-[10px]', groupStyle.text)}>
            {groupStyle.label}
          </Badge>
        </div>
      )}
    </>
  );

  if (disabled || !href) {
    return (
      <div
        className={cardClassName}
        data-testid={`tool-card-${id}`}
        aria-disabled="true"
      >
        {content}
      </div>
    );
  }

  return (
    <Link
      to={href}
      onClick={onClick}
      className={cardClassName}
      data-testid={`tool-card-${id}`}
    >
      {content}
    </Link>
  );
}
